import React from 'react';
import { Row, Col, Card, Button, Upload, Pagination, Select, Icon } from 'antd';
import { useTranslation } from 'react-i18next';

import useLibraryContents from '../../hooks/contentManager/useLibraryContents';
import useContentActions from '../../hooks/contentManager/useContentActions';

import LibraryToolbar from './LibraryToolbar';
import ContentCard from './ContentCard';
import LibraryModals from './LibraryModals';
import GenerateSkeletonComponent from '../../../../shared/components/GenerateSkeletonComponent';

import ContentsLibraryHolder from '../../styles/contentsLibraryStyle';
import Colors from '../../../../../themes/Colors';

const ContentsLibraryComponent = props => {
  const { t } = useTranslation();
  const {
    libraryContents,
    loadingContentsbyCompany,
    origin = 'createCampaign',
    listOfContentsInCampaign = []
  } = props;

  const versionSortKey = v => {
    const match = /^V(\d+)$/i.exec(v);
    return match ? parseInt(match[1], 10) : Infinity;
  };
  const existingVersions = [
    ...new Set(['V1', ...listOfContentsInCampaign.map(c => c.content_version_name || 'V1')])
  ].sort((a, b) => versionSortKey(a) - versionSortKey(b));
  const usedNumbers = existingVersions.map(versionSortKey).filter(n => n !== Infinity);
  let nextNumber = (usedNumbers.length ? Math.max(...usedNumbers) : 1) + 1;
  while (existingVersions.includes(`V${nextNumber}`)) nextNumber += 1;
  const versions = [...existingVersions, `V${nextNumber}`];

  const libraryState = useLibraryContents(props);
  const contentActions = useContentActions(props, libraryState);

  const {
    containerRef,
    pageContents,
    totalContent,
    lengthPage,
    rowCount,
    rowOptions,
    isMobile,
    tagsContents,
    selectTagContent,
    selectVersion,
    loading,
    uploadProps,
    changePageContents,
    handleRowCountChange,
    onChangeFolder,
    onChangeVersion,
    onSearch,
    isOwnerDefaultContent
  } = libraryState;

  const {
    contentModal,
    visibleModalContent,
    visibleModalDeleteContent,
    isModalOpen,
    isModalCreateVersionOpen,
    visibleCanvaModal,
    alertModalVisible,
    conflictModalOpen,
    conflictGroups,
    existingByResolution,
    paymentStatus,
    isFolderUsed,
    loadingAddAll,
    showModalContent,
    cancelModalContent,
    showModalDeleteContent,
    updateContent,
    checkContentsAdded,
    addContents,
    replaceVersion,
    createVersion,
    handleCancel,
    handleModalCreateVersionCancel,
    handleChangeAd,
    getChangeAdDescription,
    setVisibleCanvaModal,
    setAlertModalVisible,
    confirmAddAll,
    cancelAddAll,
    triggerAddAllFromFolder
  } = contentActions;

  const firstItem = totalContent > 0 ? (pageContents - 1) * lengthPage + 1 : 0;
  const lastItem = Math.min(pageContents * lengthPage, totalContent);
  const totalPages = Math.ceil(totalContent / lengthPage) || 1;

  const uploadButton = (
    <Card
      className="contentCardContent"
      style={{
        backgroundColor: Colors.surfaceMuted,
        width: '134px',
        minHeight: '200px',
        padding: '12px 12px 8px 12px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '7px',
        borderRadius: '4px'
      }}
    >
      <Button
        shape="circle"
        className="icon-card cardUpload icon-size"
        icon={loading ? 'loading' : 'cloud-upload'}
      >
        <span style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {!selectTagContent || !selectTagContent.key ? (
            <p
              className="textCard centerCard"
              style={{ color: Colors.red, textAlign: 'center', fontSize: '11px' }}
            >
              {t('Upload message 1')} <br />
              {t('Upload message 2')} <br />
              {t('Upload message 3')} <br />
            </p>
          ) : (
            <p className="textCard centerCard" style={{ textAlign: 'center', fontSize: '12px' }}>
              {t('Upload')} <br />
              {t('Upload continue')} <br />
              {t('Upload continue 2')}
            </p>
          )}
        </span>
      </Button>
    </Card>
  );

  return (
    <ContentsLibraryHolder
      lengthCard={
        !loadingContentsbyCompany && libraryContents.data && libraryContents.data.length === 0
          ? 300
          : 125
      }
    >
      <Row type="flex" className="contents-library" gutter={40}>
        <Col xs={24} sm={24} md={24} lg={24}>
          <LibraryToolbar
            tagsContents={tagsContents}
            selectTagContent={selectTagContent}
            onChangeFolder={onChangeFolder}
            selectVersion={selectVersion}
            onChangeVersion={onChangeVersion}
            versions={versions}
            onSearch={onSearch}
            onOpenCanva={() => setVisibleCanvaModal(true)}
            onAddAllFromFolder={triggerAddAllFromFolder}
            loadingAddAll={loadingAddAll}
            isFolderUsed={isFolderUsed}
            totalContent={totalContent}
            editCampaignActive={props.editCampaignActive}
            currentCampaign={props.currentCampaign}
            origin={origin}
          />
        </Col>

        <Col xs={24} sm={24} md={24} lg={24} className="libCard">
          <div ref={containerRef} style={{ width: '100%' }}>
          <Row className="contentCard" type="flex" justify="center">
            {isOwnerDefaultContent(selectTagContent) && (
              <div
                className={`contentVideoUpload ${
                  !selectTagContent || !selectTagContent.key ? 'disabledVideoUpload' : ''
                }`}
              >
                <Upload {...uploadProps} disabled={loading}>
                  {uploadButton}
                </Upload>
              </div>
            )}

            {!libraryContents.data || !selectTagContent ? (
              <GenerateSkeletonComponent count={lengthPage} height={218.3} width={141.3} />
            ) : (
              libraryContents.data.map(content => (
                <ContentCard
                  key={content.id}
                  content={content}
                  origin={origin}
                  isAdded={checkContentsAdded(content.id)}
                  onAdd={addContents}
                  onShowInfo={showModalContent}
                  onDelete={showModalDeleteContent}
                />
              ))
            )}
          </Row>
          </div>
        </Col>

        <Col xs={24} className="pagination">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            <Pagination
              current={pageContents}
              pageSize={lengthPage}
              total={totalContent}
              onChange={changePageContents}
              itemRender={(page, type, originalElement) => {
                if (type === 'prev') return <Button size="small" icon="left" disabled={pageContents <= 1} />;
                if (type === 'next') return <Button size="small" icon="right" disabled={pageContents >= totalPages} />;
                return originalElement;
              }}
            />
            {!isMobile() && (
              <Select
                value={rowCount}
                onChange={handleRowCountChange}
                style={{ width: 180, position: 'absolute', right: 0 }}
              >
                {rowOptions.map(opt => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            )}
          </div>
          <Row type="flex" justify="center" style={{ marginTop: '4px' }}>
            <span style={{ fontSize: '12px', color: Colors.textMuted }}>
              {firstItem} - {lastItem} {t('of')} {totalContent}
            </span>
          </Row>
        </Col>
      </Row>

      <LibraryModals
        visibleModalContent={visibleModalContent}
        visibleModalDeleteContent={visibleModalDeleteContent}
        isModalOpen={isModalOpen}
        isModalCreateVersionOpen={isModalCreateVersionOpen}
        visibleCanvaModal={visibleCanvaModal}
        alertModalVisible={alertModalVisible}
        conflictModalOpen={conflictModalOpen}
        contentModal={contentModal}
        selectTagContent={selectTagContent}
        getChangeAdDescription={getChangeAdDescription}
        conflictGroups={conflictGroups}
        existingByResolution={existingByResolution}
        paymentStatus={paymentStatus}
        cancelModalContent={cancelModalContent}
        updateContent={updateContent}
        showModalDeleteContent={showModalDeleteContent}
        replaceVersion={replaceVersion}
        handleCancel={handleCancel}
        createVersion={createVersion}
        handleModalCreateVersionCancel={handleModalCreateVersionCancel}
        handleChangeAd={handleChangeAd}
        setAlertModalVisible={setAlertModalVisible}
        setVisibleCanvaModal={setVisibleCanvaModal}
        confirmAddAll={confirmAddAll}
        cancelAddAll={cancelAddAll}
      />
    </ContentsLibraryHolder>
  );
};

export default ContentsLibraryComponent;
